import { Overlay } from 'app/components/common/overlay'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { of, Subject, from } from 'rxjs'
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators'

import { Input } from '../input'

import styles from './autocomplete.module.scss'

const OptionsList = ({
  OptionComp,
  options,
  optionSelected,
  emptyLabel,
  className = '',
  ...rest
}) => {
  let empty = null

  if (emptyLabel && !options?.length) {
    empty = (
      <li key="__empty" className={styles.optionList__empty}>
        {emptyLabel}
      </li>
    )
  }

  return (
    <ul className={`${styles.autocomplete__options} ${className}`} {...rest}>
      {empty}
      {options?.map((option, idx) => (
        <li key={idx} onClick={() => optionSelected(option)}>
          <OptionComp option={option} />
        </li>
      ))}
    </ul>
  )
}

OptionsList.propTypes = {
  OptionComp: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
  idBy: PropTypes.string,
  className: PropTypes.string,
  optionSelected: PropTypes.func,
  emptyLabel: PropTypes.string,
}

const Autocomplete = ({
  OptionComp,
  optionsResolver,
  idBy,
  displayBy,
  emptyLabel,
  value,
  resetTrigger,
  onChange,
  optionOverlayClass = '',
  className = '',
  ...rest
}) => {
  const inputRef = React.createRef()

  const [displayValue, setDisplayValue] = useState('')
  const [, setControlValue] = useState(value)
  const [loading, setLoading] = useState(false)

  const [options, setOptions] = useState([])
  const [showOptions, setShowOptions] = useState(false)

  const [valueChange$] = useState(new Subject())

  // load options or return an empty array
  const loadOptions = (token) => {
    if (token.length > 1) {
      return from(optionsResolver(token)).pipe(catchError(() => of([])))
    } else {
      return of(null)
    }
  }

  useEffect(() => {
    if (!value) {
      setDisplayValue('')
      setControlValue('')
    }
  }, [value, resetTrigger])

  useEffect(() => {
    // handle stream of input changes - reload option set
    const valueChangeSubscription = valueChange$
      .pipe(
        debounceTime(500),
        tap(() => setLoading(true)),
        switchMap((v) => loadOptions(v)),
        tap(() => setLoading(false))
      )
      .subscribe((res) => {
        setOptions(res)
        setShowOptions(!!res)
      })

    // cleanup subscription on destroy
    return () => valueChangeSubscription.unsubscribe()
  }, [])

  const emitChange = (value) => {
    if (typeof onChange === 'function') {
      onChange(value)
    }
  }

  const autocompleteChange = (ev) => {
    setDisplayValue(ev.target.value)
    setControlValue('')
    emitChange('')
    valueChange$.next(ev.target.value)
  }

  const getDisplayValue = (option) => {
    if (option) {
      if (typeof displayBy === 'function') {
        return displayBy(option)
      } else {
        return option[displayBy]
      }
    }

    return ''
  }

  const getControlValue = (option) => {
    return idBy ? option[idBy] : option
  }

  const optionSelected = (option) => {
    setOptions([])
    setDisplayValue(getDisplayValue(option))

    const resolvedValue = getControlValue(option)
    setControlValue(resolvedValue)
    emitChange(resolvedValue)
    inputRef.current.focus()
  }

  const clearValue = () => {
    setDisplayValue('')
    setControlValue('')
    emitChange('')
    inputRef.current.focus()
  }

  const loadingIndicator = {
    width: '40px',
    template: (
      <svg className={styles.autocomplete__spinner} viewBox="0 0 50 50">
        <circle
          className={styles.autocomplete__spinnerPath}
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="5"
        ></circle>
      </svg>
    ),
  }

  const clearValueIndicator = {
    width: '40px',
    template: (
      <div onClick={clearValue} className={styles.autocomplete__clearValue}>
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
          <g className={styles.autocomplete__clearValue} transform="translate(0.354 0.354)">
            <line x2="19.929" y2="19.929" fill="none" strokeWidth="1" />
            <line x1="19.929" y2="19.929" fill="none" strokeWidth="1" />
          </g>
        </svg>
      </div>
    ),
  }

  const optionsList = (
    <Overlay isOpen={showOptions} openChange={setShowOptions}>
      <OptionsList
        className={optionOverlayClass}
        OptionComp={OptionComp}
        options={options}
        optionSelected={(option) => optionSelected(option)}
        emptyLabel={emptyLabel}
      />
    </Overlay>
  )

  const suffix = loading ? loadingIndicator : displayValue ? clearValueIndicator : null

  return (
    <div className={`${styles.autocomplete} ${className}`}>
      <Input
        ref={inputRef}
        value={displayValue}
        onChange={autocompleteChange}
        suffix={suffix}
        autoComplete="off"
        {...rest}
      />
      {optionsList}
    </div>
  )
}

Autocomplete.displayName = 'Autocomplete'

Autocomplete.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  OptionComp: PropTypes.func.isRequired,
  optionOverlayClass: PropTypes.string,
  optionsResolver: PropTypes.func.isRequired,
  idBy: PropTypes.string,
  displayBy: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  emptyLabel: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  resetTrigger: PropTypes.number,
}

export { Autocomplete }
